'use client';

import { c as _c } from "react/compiler-runtime";
import { Badge } from '@common/components/atom/Badge/Badge';
import { FOCUS_RING } from '@common/components/interaction';
import { cn } from '@core/utils';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import Image from 'next/image';
import { type ReactNode, useEffect, useState } from 'react';
import { CustomLink } from '@/app/_components/CustomLink';
import { DONATION_ROUTES } from '@/constants';
import { GOODS_HOME_IMAGE_URL } from '@/constants/yearEndDonationHome';
import { YEAR_END_GOODS_STOCK_STATUS, type YearEndGoodsGift } from '@/constants/yearEndGoodsGift';
const BUBBLE_VISIBLE_MS = 5_000;

//* 펼침은 등장 easeOut·접힘은 easeIn — expo-out이면 앞에서 다 열려 '툭' 보인다
const EXPAND_TRANSITION = {
  duration: 0.24,
  ease: 'easeOut'
} as const;
const COLLAPSE_TRANSITION = {
  duration: 0.2,
  ease: 'easeIn'
} as const;

//* 배지·재고 박스는 자체 배경이 상태 색이라 INTERACTIVE_SURFACE 대신 투명도로 피드백한다
const GUIDE_LINK_FEEDBACK = cn('transition-opacity duration-150 hover:opacity-80 active:opacity-70 motion-reduce:transition-none', FOCUS_RING);
const STOCK_BOX_STYLE = {
  [YEAR_END_GOODS_STOCK_STATUS.AVAILABLE]: {
    box: 'bg-M-25',
    title: 'text-Gray-900',
    description: 'text-Gray-500',
    track: 'bg-Gray-white',
    fill: 'bg-M-400',
    caption: 'text-M-600'
  },
  [YEAR_END_GOODS_STOCK_STATUS.CLOSING]: {
    box: 'bg-R-25',
    title: 'text-Gray-900',
    description: 'text-Gray-500',
    track: 'bg-Gray-white',
    fill: 'bg-R-500',
    caption: 'text-R-550'
  },
  [YEAR_END_GOODS_STOCK_STATUS.SOLD_OUT]: {
    box: 'bg-Gray-50',
    title: 'text-Gray-500',
    description: 'text-Gray-400',
    track: 'bg-Gray-300',
    fill: 'bg-Gray-300',
    caption: 'text-Gray-500'
  }
};

//* 수량·옵션으로 대상 금액을 넘나들 때 높이를 보간해 아래 내용이 밀리지 않고 흐르게 한다 — 다 열린 뒤엔 포커스 링이 잘리지 않게 overflow를 푼다
export const GoodsReveal = t0 => {
  const $ = _c(6);
  const {
    isShown,
    children
  } = t0;
  const isReducedMotion = useReducedMotion();
  let t1;
  if ($[0] !== children || $[1] !== isReducedMotion || $[2] !== isShown) {
    t1 = isShown ? <motion.div key="goods" initial={{
      height: 0,
      opacity: 0,
      overflow: "hidden"
    }} animate={{
      height: "auto",
      opacity: 1,
      transition: isReducedMotion ? {
        duration: 0
      } : EXPAND_TRANSITION,
      transitionEnd: {
        overflow: "visible"
      }
    }} exit={{
      height: 0,
      opacity: 0,
      overflow: "hidden",
      transition: isReducedMotion ? {
        duration: 0
      } : COLLAPSE_TRANSITION
    }}>{children}</motion.div> : null;
    $[0] = children;
    $[1] = isReducedMotion;
    $[2] = isShown;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <AnimatePresence initial={false}>{t1}</AnimatePresence>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};
export const hasGoodsDiary = (goodsGift: YearEndGoodsGift) => goodsGift.stockStatus !== YEAR_END_GOODS_STOCK_STATUS.SOLD_OUT;
export const GoodsEventLabel = t0 => {
  const $ = _c(5);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("block w-fit rounded-small", GUIDE_LINK_FEEDBACK, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Badge className="w-fit bg-M-400 text-Gray-white">웰로x카카오프렌즈굿즈 이벤트</Badge>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <CustomLink href={DONATION_ROUTES.GUIDE.pathname} className={t1}>{t2}</CustomLink>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
export const GoodsStockBox = t0 => {
  const $ = _c(49);
  const {
    goodsGift,
    className
  } = t0;
  const {
    remainingPercent,
    stockStatus
  } = goodsGift;
  const style = STOCK_BOX_STYLE[stockStatus];
  const isSoldOut = stockStatus === YEAR_END_GOODS_STOCK_STATUS.SOLD_OUT;
  const stockLabel = isSoldOut ? "\uC794\uC5EC 0% \uB9C8\uAC10" : stockStatus === YEAR_END_GOODS_STOCK_STATUS.CLOSING ? `잔여 ${remainingPercent}% 마감임박` : `잔여 ${remainingPercent}%`;
  let t1;
  if ($[0] !== className || $[1] !== style.box) {
    t1 = cn("flex items-center gap-16 rounded-medium p-14", style.box, GUIDE_LINK_FEEDBACK, className);
    $[0] = className;
    $[1] = style.box;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = isSoldOut && "opacity-50";
  let t3;
  if ($[3] !== t2) {
    t3 = cn("object-contain", t2);
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <div className="relative size-100 shrink-0 overflow-hidden rounded-small"><Image src={GOODS_HOME_IMAGE_URL.BOOK_OPEN} alt="" fill={true} sizes="100px" className={t3} /></div>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== style.title) {
    t5 = cn("font-700 typo-body4", style.title);
    $[7] = style.title;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <p className={t5}>웰로 x 카카오프렌즈 2027 다이어리</p>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== style.description) {
    t7 = cn("font-400 typo-caption1", style.description);
    $[11] = style.description;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = isSoldOut ? "\uC900\uBE44\uB41C \uC218\uB7C9\uC774 \uBAA8\uB450 \uC18C\uC9C4\uB410\uC5B4\uC694" : "\uAE30\uBD80 \uC644\uB8CC \uD6C4 7\uC77C \uC774\uB0B4 \uC21C\uCC28 \uBC1C\uC1A1";
  let t9;
  if ($[13] !== t7 || $[14] !== t8) {
    t9 = <p className={t7}>{t8}</p>;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t6 || $[17] !== t9) {
    t10 = <div className="flex flex-col gap-2">{t6}{t9}</div>;
    $[16] = t6;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== style.track) {
    t11 = cn("h-6 w-full overflow-hidden rounded-pill", style.track);
    $[19] = style.track;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== style.fill) {
    t12 = cn("h-full rounded-pill", style.fill);
    $[21] = style.fill;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const t13 = `${remainingPercent}%`;
  let t14;
  if ($[23] !== t13) {
    t14 = {
      width: t13
    };
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t12 || $[26] !== t14) {
    t15 = <div className={t12} style={t14} />;
    $[25] = t12;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t11 || $[29] !== t15) {
    t16 = <div className={t11}>{t15}</div>;
    $[28] = t11;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== style.caption) {
    t17 = cn("flex items-center justify-between gap-6 font-600 typo-caption1", style.caption);
    $[31] = style.caption;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <span>한정 굿즈</span>;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== stockLabel) {
    t19 = <span className="whitespace-nowrap">{stockLabel}</span>;
    $[34] = stockLabel;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== t17 || $[37] !== t19) {
    t20 = <p className={t17}>{t18}{t19}</p>;
    $[36] = t17;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t16 || $[40] !== t20) {
    t21 = <div className="flex flex-col gap-6">{t16}{t20}</div>;
    $[39] = t16;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== t10 || $[43] !== t21) {
    t22 = <div className="flex min-w-0 flex-1 flex-col gap-20">{t10}{t21}</div>;
    $[42] = t10;
    $[43] = t21;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== t1 || $[46] !== t22 || $[47] !== t4) {
    t23 = <CustomLink href={DONATION_ROUTES.GUIDE.pathname} className={t1}>{t4}{t22}</CustomLink>;
    $[45] = t1;
    $[46] = t22;
    $[47] = t4;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  return t23;
};
export const GoodsDonateBubble = () => {
  const $ = _c(19);
  const [isVisible, setIsVisible] = useState(true);
  const isReducedMotion = useReducedMotion();
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const timer = window.setTimeout(() => setIsVisible(false), BUBBLE_VISIBLE_MS);
      return () => window.clearTimeout(timer);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  const t2 = !isVisible;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      opacity: 0
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = isVisible ? 0.8 : 0;
  let t5;
  if ($[3] !== isReducedMotion || $[4] !== isVisible) {
    t5 = isReducedMotion ? {
      duration: 0
    } : isVisible ? EXPAND_TRANSITION : COLLAPSE_TRANSITION;
    $[3] = isReducedMotion;
    $[4] = isVisible;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t4 || $[7] !== t5) {
    t6 = {
      opacity: t4,
      transition: t5
    };
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== isReducedMotion) {
    t7 = isReducedMotion ? {
      duration: 0
    } : COLLAPSE_TRANSITION;
    $[9] = isReducedMotion;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t7) {
    t8 = {
      opacity: 0,
      transition: t7
    };
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <p className="whitespace-nowrap rounded-xsmall bg-Gray-black p-10 font-400 text-Gray-white typo-caption1">지금 기부하면 카카오프렌즈 다이어리 증정!</p>;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <svg width={14} height={8} viewBox="0 0 14 8" className="ml-12 text-Gray-black" aria-hidden={true}><path d="M0 0h14L7 8z" fill="currentColor" /></svg>;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t2 || $[16] !== t6 || $[17] !== t8) {
    t11 = <motion.div aria-hidden={t2} initial={t3} animate={t6} exit={t8} className="pointer-events-none absolute bottom-[calc(100%-4px)] left-1/2 flex -translate-x-1/2 flex-col items-start">{t9}{t10}</motion.div>;
    $[15] = t2;
    $[16] = t6;
    $[17] = t8;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  return t11;
};